import { Injectable } from '@angular/core';

import { ReturnsMessageConfig } from '@frontend/sports/common/client-config-data-access';
import { UserService } from '@frontend/sports/user/feature';
import { isEmpty } from 'lodash-es';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { getPicksCount } from '../../../core/utils';
import { IBetBuilderPickState } from '../../bet-builder/models';
import { ISingleBetState } from '../../single-bet/state';
import { IBetslipCalculatorOverrides } from '../models';
import { ComboSlipType, SingleSlipType, SlipType } from '../slip-type';
import { BetslipStateOverrides } from './base-input-builder';

@Injectable()
export default class BetslipBarInputBuilderService {
    constructor(
        private userService: UserService,
        private multiReturnsMessageConfig: ReturnsMessageConfig,
    ) {}

    /**
     * For the labels that have default empty stake betslip bar by design should calculate possible winnings for single & multi bets based
     * on multi-returns config default stake (similar to multi-returns message component but also handle single bets)
     *
     * @param param0 betslip state
     * @returns Stake-specific overrides
     */
    getBetslipBarOverrides(state: BetslipStateOverrides, isSportcastAsComboEnabled: boolean): IBetslipCalculatorOverrides {
        const overrides: IBetslipCalculatorOverrides = {
            slipOverrides: this.getSlipOverrides(state, isSportcastAsComboEnabled),
            pickOverrides: {
                prices: {},
                eachWay: {},
                replacements: {},
            },
        };

        return overrides;
    }

    getSlipOverrides(state: BetslipStateOverrides, isSportcastAsComboEnabled: boolean) {
        return [
            {
                stake: { value: this.getSelectedStake(state) ?? this.getFallbackStake() },
                system: this.getUnderlyingSlipType(state, isSportcastAsComboEnabled),
                pickIds: this.getPicksId(state) ?? [],
            },
        ];
    }

    private getSelectedStake(state: BetslipStateOverrides) {
        if (this.isSingleTypeSelected(state)) {
            return this.getSingleStake(state.types.singleBet);
        } else if (state.types.comboBet.stake) {
            return state.types.comboBet.stake;
        } else {
            return this.getBetBuilderStake(state.types.betBuilder.picks);
        }
    }

    private getPicksId(state: BetslipStateOverrides) {
        return (this.isSingleTypeSelected(state) ? Object.keys(state.types.singleBet.picks) : Object.keys(state.types.comboBet.picks))?.filter((t) =>
            state.picks.pickList.find((p) => p.id.toString() === t),
        );
    }

    private getSingleStake(singleBetState: ISingleBetState): number | null {
        const picks = Object.values(singleBetState.picks);
        // we don't calculate summary when no picks or multi singles
        if (picks.length !== 1) {
            return null;
        }

        return picks[0].stake;
    }

    private getBetBuilderStake(betBuilderPicks: { [pickId: string]: IBetBuilderPickState }): number | null {
        if (isEmpty(betBuilderPicks)) {
            return null;
        }
        const [_, betBuilderBetPick] = Object.entries(betBuilderPicks)[0];

        return betBuilderBetPick.stake;
    }

    private getFallbackStake(): number {
        const multiReturnStakePerCurrencyMap = this.multiReturnsMessageConfig.defaultStakes;
        const currency = this.userService.currency.toLowerCase();

        return multiReturnStakePerCurrencyMap[currency] ?? 0;
    }

    getUnderlyingSlipType(state: BetslipStateOverrides, isSportcastAsComboEnabled: boolean): SlipType {
        if (this.isSingleTypeSelected(state)) {
            return new SingleSlipType();
        }
        const isSingleBetBuilderPick = getPicksCount(state.picks.pickList) === 1 && BetslipBetBuilderPick.isPick(state.picks.pickList[0]);

        return isSportcastAsComboEnabled && isSingleBetBuilderPick ? new SingleSlipType() : new ComboSlipType();
    }

    private isSingleTypeSelected(state: BetslipStateOverrides) {
        return state.types.base.currentSelectedType === BetslipType.Single;
    }
}
